import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { equals, uniq } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import {
  DisplaySettingsRounded,
  KeyboardArrowDownRounded,
  Settings,
} from '@mui/icons-material'
import {
  Box,
  Button,
  ButtonBase,
  Card,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { humanize } from 'inflection'
import { DateTime, Duration } from 'luxon'

import { Breadcrumbs, Loading } from '@common/components'
import MetricsSelector from '@common/components/MetricsSelector/MetricsSelector'
import { parseApiErrors, useLocalStorage, useSmallScreen } from '@common/utils'
import NavigationTabs from '@rest/UI/Navigation/NavigationTabs'

import AggregationSelector from './components/AggregationSelector'
import DateRangeSelector from './components/DateRangeSelector'
import DisplaySelector, {
  METRICS_DISPLAY_GRAPH,
  METRICS_DISPLAY_TABLE,
} from './components/DisplaySelector'
import GroupingSelector, {
  METRICS_GROUPING_PROPERTY,
  METRICS_GROUPING_PROPERTY_GROUP,
} from './components/GroupingSelector'
import PropertySelector from './components/PropertySelector'
import getSmokeMetricsConstants from './constants'
import CustomDateModal from './CustomDateModal'
import ExportMetrics from './ExportMetrics'
import GroupedByGroupingTypeView from './GroupedByGroupingTypeView'
import GroupedByMetricView from './GroupedByMetricView'
import metrics from './metrics'
import MonthWeekModal from './MonthWeekModal'

const { availableIntervals, datePresets, weeklyRanges, monthlyRanges } =
  getSmokeMetricsConstants()

export default function SmokeMetrics() {
  const isSmallScreen = useSmallScreen()
  const theme = useTheme()

  const {
    smokeMetrics,
    smokeMetricsIsLoading,
    smokeMetricsError,
    smokeMetricProperties: availableProperties,
    currentOrganizationDetails: organization,
    reportMetrics,
    doFetchSmokeMetrics,
    doFetchSmokeMetricsProperties,
    doResetSmokeMetricsState,
    doShowSnackbar,
    doSetHeaderLevel,
  } = useConnect(
    'selectSmokeMetrics',
    'selectSmokeMetricsIsLoading',
    'selectSmokeMetricsError',
    'selectSmokeMetricProperties',
    'selectCurrentOrganizationDetails',
    'selectReportMetrics',
    'doFetchSmokeMetrics',
    'doFetchSmokeMetricsProperties',
    'doResetSmokeMetricsState',
    'doShowSnackbar',
    'doSetHeaderLevel',
  )

  const [selectedProperties, setSelectedProperties] = useState([])

  const [selectedAggregationRange, setSelectedAggregationRange] = useState(4)
  const [selectedInterval, setSelectedInterval] = useState(availableIntervals.weekly)
  const [selectedView, setSelectedView] = useState(METRICS_DISPLAY_TABLE)
  const [selectedGrouping, setSelectedGrouping] = useState(METRICS_GROUPING_PROPERTY)
  const [selectedMetrics, setSelectedMetrics] = useLocalStorage(
    'restEnabledMetrics',
    {},
  )
  const [datePreset, setDatePreset] = useState(datePresets.threeMonths.value)
  const [openCustomDate, setOpenCustomDate] = useState(false)
  const [openMonthWeekModal, setOpenMonthWeekModal] = useState(false)
  const [settingsAnchor, setSettingsAnchor] = useState(null)
  const [enabledMetricsAnchor, setEnabledMetricsAnchor] = useState(null)
  const [menuCardOpen, setMenuCardOpen] = useState(true)

  const [showTotal, setShowTotal] = useState(false)
  const [showPercentageChange, setShowPercentageChange] = useState(false)
  const [groupByGroupingType, setGroupByGroupingType] = useState(false)

  useEffect(() => {
    if (availableProperties) {
      const allowGrouping =
        availableProperties?.map((property) => property.group).length > 1
      setGroupByGroupingType(availableProperties.length === 1)
      setSelectedGrouping(
        allowGrouping ? METRICS_GROUPING_PROPERTY_GROUP : METRICS_GROUPING_PROPERTY,
      )
    }
  }, [availableProperties])

  const enableGrouping = useMemo(
    () => availableProperties?.map((property) => property.group).length > 1,
    [availableProperties],
  )

  const calculateWeeksIntervalTimeframe = useCallback(
    (now) => {
      const offset = Duration.fromObject({ days: 1 })
      const start = now
        .minus({ weeks: selectedAggregationRange - 1 })
        .plus(offset)
        .startOf('week')
        .minus(offset)
      const end = now.endOf('week').minus(offset)
      return [start, end]
    },
    [selectedAggregationRange],
  )

  const [defaultStart, defaultEnd] = calculateWeeksIntervalTimeframe(DateTime.now())
  const [dateRangeValue, setDateRangeValue] = useState([defaultStart, defaultEnd])

  const requestParams = useRef({})

  const allowedMetrics = useMemo(
    () =>
      [
        metrics.incidentRate,
        metrics.netCharges,
        metrics.netChargesPerEvent,
        metrics.netChargesPerDevice,
        metrics.netChargesPerChargedEvent,
        metrics.missedRevenue,
        metrics.chargeRate,
        metrics.grossChargeRate,
        metrics.adjustmentRate,
        metrics.eventsTotal,
        metrics.eventsCharged,
        metrics.connectivity,
      ].map((metric) => {
        const systemMetric = reportMetrics?.find((obj) => obj.key === metric.apiValue)
        const humanizedMetric = systemMetric?.name ?? humanize(metric.apiValue)
        return { ...metric, name: humanizedMetric }
      }),
    [reportMetrics],
  )

  useEffect(() => {
    // make sure that newly added metrics will be enabled by default
    setSelectedMetrics((prev) => {
      const updated = { ...prev }
      allowedMetrics.forEach((metric) => {
        if (updated[metric.apiValue] === undefined) {
          updated[metric.apiValue] = true
        }
      })
      return updated
    })
  }, [allowedMetrics])

  useEffect(() => {
    if (selectedInterval !== 1) {
      const now = DateTime.now()
      let start
      let end
      if (selectedInterval.value === 30) {
        start = now.minus({ months: selectedAggregationRange - 1 }).startOf('month')
        end = now.endOf('month')
      } else if (selectedInterval.value === 7) {
        const [calcStart, calcEnd] = calculateWeeksIntervalTimeframe(now)
        start = calcStart
        end = calcEnd
      }
      setDateRangeValue([start, end])
    }
  }, [selectedInterval, selectedAggregationRange])

  const fetchMetrics = async (params) => {
    const properties = params?.properties ?? selectedProperties
    if (properties.length !== 0 && dateRangeValue.every((value) => value !== null)) {
      requestParams.current = {
        start: dateRangeValue[0].toFormat('yyyy-MM-dd'),
        end: dateRangeValue[1].toFormat('yyyy-MM-dd'),
        intervalType: selectedInterval.type,
        interval: 1,
        metrics: allowedMetrics.map((item) => item.apiValue),
      }

      if (selectedGrouping === METRICS_GROUPING_PROPERTY_GROUP) {
        requestParams.current.grouping = METRICS_GROUPING_PROPERTY_GROUP
        requestParams.current.accounts = uniq(
          properties.map((property) => property.account),
        )
      } else {
        requestParams.current.grouping = METRICS_GROUPING_PROPERTY
        requestParams.current.properties = properties.map((property) => property.id)
      }

      try {
        await doFetchSmokeMetrics(
          requestParams.current,
          selectedInterval.type,
          selectedInterval.value,
        )
      } catch (err) {
        const parsedError = parseApiErrors(err?.response)
        doShowSnackbar(parsedError, 'error')
      }
    }
  }

  useEffect(() => {
    doSetHeaderLevel('organization')
    async function fetchData() {
      try {
        const properties = await doFetchSmokeMetricsProperties()
        setSelectedProperties(properties ?? [])
        fetchMetrics({ properties })
      } catch (err) {
        const parsedError = parseApiErrors(err?.response)
        doShowSnackbar(parsedError, 'error')
      }
    }
    fetchData()
    return () => doResetSmokeMetricsState()
  }, [])

  useEffect(() => {
    fetchMetrics()
  }, [selectedGrouping, dateRangeValue])

  useEffect(() => {
    if (smokeMetricsError) {
      const parsedError = parseApiErrors(smokeMetricsError?.response)
      doShowSnackbar(parsedError, 'error')
    }
  }, [smokeMetricsError])

  const isEmptyResult = smokeMetrics ? Object.keys(smokeMetrics).length === 0 : true

  const handlePresetChange = (value) => {
    if (value === datePresets.custom.value) {
      setOpenCustomDate(true)
      return
    }

    let start
    let end
    switch (value) {
      case datePresets.week.value: {
        start = DateTime.now().minus({ days: 7 })
        end = DateTime.now()
        break
      }
      case datePresets.month.value: {
        start = DateTime.now().minus({ month: 1 })
        end = DateTime.now()
        break
      }
      case datePresets.twoMonths.value: {
        start = DateTime.now().minus({ month: 2 })
        end = DateTime.now()
        break
      }
      case datePresets.threeMonths.value: {
        start = DateTime.now().minus({ month: 3 })
        end = DateTime.now()
        break
      }
      case datePresets.monthToDate.value: {
        start = DateTime.now().startOf('month')
        end = DateTime.now()
        break
      }
      case datePresets.lastMonth.value: {
        const prevMonth = DateTime.now().minus({ month: 1 })
        start = prevMonth.startOf('month')
        end = prevMonth.endOf('month')
        break
      }
      case datePresets.yearToDate.value: {
        start = DateTime.now().startOf('year')
        end = DateTime.now()
        break
      }
      default:
        throw Error(`${value} preset is not supported`)
    }
    setDatePreset(value)
    setDateRangeValue([start, end])
  }

  const aggregatedDateRanges = useMemo(() => {
    if (selectedInterval.value !== 1) {
      return selectedInterval.value === 7 ? weeklyRanges : monthlyRanges
    }
    return null
  }, [selectedInterval])

  const handleAggregatedDateRangeChange = (value) => {
    if (value === datePresets.custom.value) {
      setOpenMonthWeekModal(true)
      return
    }
    setSelectedAggregationRange(value)
  }

  const visibleDatePresetKeys = (() => {
    if (equals(selectedInterval, availableIntervals.weekly)) {
      return Object.keys(datePresets).filter(
        (key) => datePresets[key].value !== datePresets.week.value,
      )
    }
    if (equals(selectedInterval, availableIntervals.monthly)) {
      return Object.keys(datePresets).filter(
        (key) =>
          ![datePresets.week.value, datePresets.month.value].includes(
            datePresets[key].value,
          ),
      )
    }
    return Object.keys(datePresets)
  })()

  const groupedByEntityData = useMemo(() => {
    if (!smokeMetrics) return {}

    const metricsEntries = Object.entries(smokeMetrics).filter(
      ([key]) => selectedMetrics[key],
    )
    const result = {}
    const summary = []

    metricsEntries.forEach((entries) => {
      entries[1].data.forEach((data) => {
        const key = [...data.key.split(':').slice(0, 2), data.name].join(':')

        const systemMetric = reportMetrics?.find((obj) => obj.key === data.metric)
        const humanizedMetric = systemMetric?.name ?? humanize(data.metric)
        const mData = { ...data, name: humanizedMetric }
        if (key in result) {
          result[key].push(mData)
        } else {
          result[key] = [mData]
        }
      })

      summary.push({
        ...entries[1].summary,
        key: `summary:${entries[1].summary.metric}`,
      })
    })
    return { data: result, summary }
  }, [smokeMetrics, selectedMetrics])

  useEffect(() => {
    if (selectedInterval.value === 1) {
      handlePresetChange(datePresets.threeMonths.value)
    }
  }, [selectedInterval])

  return (
    <>
      <MonthWeekModal
        open={openMonthWeekModal}
        entity={selectedInterval.value === 7 ? 'week' : 'month'}
        onCancel={() => setOpenMonthWeekModal(false)}
        onSave={(value) => {
          setSelectedAggregationRange(value)
          setOpenMonthWeekModal(false)
        }}
      />
      <CustomDateModal
        open={openCustomDate}
        onCancel={() => setOpenCustomDate(false)}
        onSave={(dateRange) => {
          setDateRangeValue(dateRange)
          setDatePreset(datePresets.custom.value)
          setOpenCustomDate(false)
        }}
      />
      <Popover
        open={!!enabledMetricsAnchor}
        onClose={() => setEnabledMetricsAnchor(null)}
        anchorEl={enabledMetricsAnchor}
        anchorOrigin={{ vertical: 40, horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MetricsSelector
          allowedMetrics={allowedMetrics}
          selectedMetrics={selectedMetrics}
          setSelectedMetrics={setSelectedMetrics}
        />
      </Popover>
      <Popover
        open={!!settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        anchorEl={settingsAnchor}
        anchorOrigin={{ vertical: 40, horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack direction="column" px={2} py={0.5}>
          {selectedView === METRICS_DISPLAY_GRAPH && (
            <FormControlLabel
              slotProps={{ typography: { fontSize: 14 } }}
              control={
                <Checkbox
                  checked={showTotal}
                  size="small"
                  onChange={() => setShowTotal((v) => !v)}
                />
              }
              label="Show total"
            />
          )}
          {selectedView === METRICS_DISPLAY_TABLE && (
            <>
              <FormControlLabel
                slotProps={{ typography: { fontSize: 14 } }}
                control={
                  <Checkbox
                    checked={groupByGroupingType}
                    size="small"
                    onChange={() => setGroupByGroupingType((v) => !v)}
                  />
                }
                label={`Group by ${humanize(selectedGrouping).toLowerCase()}`}
              />
              <FormControlLabel
                slotProps={{ typography: { fontSize: 14 } }}
                control={
                  <Checkbox
                    checked={showPercentageChange}
                    size="small"
                    onChange={() => setShowPercentageChange((v) => !v)}
                  />
                }
                label="Show percentage change"
              />
            </>
          )}
        </Stack>
      </Popover>
      <Box>
        <Breadcrumbs
          links={[{ label: organization?.name || '--' }, { label: 'Metrics' }]}
        />
        <NavigationTabs />

        {/* Desktop Header Start */}
        {!isSmallScreen.mobile && (
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="end" gap={2}>
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Tooltip title="Toggle available metrics" placement="right">
                  <span>
                    <IconButton
                      onClick={(e) => setEnabledMetricsAnchor(e.currentTarget)}
                      disabled={smokeMetricsIsLoading}
                    >
                      <DisplaySettingsRounded />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                  disabled={smokeMetricsIsLoading}
                >
                  <Settings />
                </IconButton>
              </Box>
              <PropertySelector
                disabled={smokeMetricsIsLoading}
                availableProperties={availableProperties}
                selectedProperties={selectedProperties}
                setSelectedProperties={setSelectedProperties}
                enableGrouping={enableGrouping}
                setSelectedGrouping={setSelectedGrouping}
                fetchMetrics={fetchMetrics}
              />
              <AggregationSelector
                disabled={smokeMetricsIsLoading}
                availableIntervals={availableIntervals}
                selectedInterval={selectedInterval}
                setSelectedInterval={setSelectedInterval}
              />
              <DisplaySelector
                disabled={smokeMetricsIsLoading}
                selectedView={selectedView}
                setSelectedView={setSelectedView}
              />
              {enableGrouping && (
                <GroupingSelector
                  disabled={smokeMetricsIsLoading}
                  selectedGrouping={selectedGrouping}
                  setSelectedGrouping={setSelectedGrouping}
                />
              )}
            </Box>
            <Box display="flex" alignItems="end">
              <DateRangeSelector
                disabled={smokeMetricsIsLoading}
                selectedInterval={selectedInterval}
                selectedAggregationRange={selectedAggregationRange}
                aggregatedDateRanges={aggregatedDateRanges}
                dateRangeValue={dateRangeValue}
                datePreset={datePreset}
                visibleDatePresetKeys={visibleDatePresetKeys}
                datePresets={datePresets}
                handlePresetChange={handlePresetChange}
                handleAggregatedDateRangeChange={handleAggregatedDateRangeChange}
              />
              {requestParams.current?.start && (
                <ExportMetrics
                  requestParams={requestParams.current}
                  intervalType={selectedInterval.type}
                  interval={selectedInterval.value}
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </Box>
        )}
        {/* Desktop Header End */}

        {/* Mobile Header Start */}
        {isSmallScreen.mobile && (
          <Card sx={{ mb: 2 }}>
            <ButtonBase
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: 1,
                px: 2,
                py: 2,
              }}
              onClick={() => setMenuCardOpen((prev) => !prev)}
            >
              <Typography variant="h6">Dashboard Menu</Typography>
              <KeyboardArrowDownRounded
                sx={{
                  transform: menuCardOpen ? 'rotate(180deg)' : null,
                  transition: theme.transitions.create('transform', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
                }}
              />
            </ButtonBase>
            <Collapse in={menuCardOpen}>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                mb={2}
                mx={2}
              >
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" flexDirection="row" gap={2}>
                    <Button
                      fullWidth
                      onClick={(e) => setEnabledMetricsAnchor(e.currentTarget)}
                      disabled={smokeMetricsIsLoading}
                      variant="outlined"
                      startIcon={<DisplaySettingsRounded />}
                    >
                      Metrics
                    </Button>
                    <Button
                      fullWidth
                      onClick={(e) => setSettingsAnchor(e.currentTarget)}
                      disabled={smokeMetricsIsLoading}
                      variant="outlined"
                      startIcon={<Settings />}
                    >
                      Settings
                    </Button>
                    {requestParams.current?.start && (
                      <ExportMetrics
                        requestParams={requestParams.current}
                        intervalType={selectedInterval.type}
                        interval={selectedInterval.value}
                      />
                    )}
                  </Box>
                  <PropertySelector
                    disabled={smokeMetricsIsLoading}
                    availableProperties={availableProperties}
                    selectedProperties={selectedProperties}
                    setSelectedProperties={setSelectedProperties}
                    enableGrouping={enableGrouping}
                    setSelectedGrouping={setSelectedGrouping}
                    fetchMetrics={fetchMetrics}
                  />
                  <Box display="flex" gap={2}>
                    <DisplaySelector
                      disabled={smokeMetricsIsLoading}
                      selectedView={selectedView}
                      setSelectedView={setSelectedView}
                    />
                    <AggregationSelector
                      disabled={smokeMetricsIsLoading}
                      availableIntervals={availableIntervals}
                      selectedInterval={selectedInterval}
                      setSelectedInterval={setSelectedInterval}
                    />
                    {enableGrouping && (
                      <GroupingSelector
                        disabled={smokeMetricsIsLoading}
                        selectedGrouping={selectedGrouping}
                        setSelectedGrouping={setSelectedGrouping}
                      />
                    )}
                  </Box>
                  <DateRangeSelector
                    disabled={smokeMetricsIsLoading}
                    selectedInterval={selectedInterval}
                    selectedAggregationRange={selectedAggregationRange}
                    aggregatedDateRanges={aggregatedDateRanges}
                    dateRangeValue={dateRangeValue}
                    datePreset={datePreset}
                    visibleDatePresetKeys={visibleDatePresetKeys}
                    datePresets={datePresets}
                    handlePresetChange={handlePresetChange}
                    handleAggregatedDateRangeChange={handleAggregatedDateRangeChange}
                  />
                </Box>
              </Box>
            </Collapse>
          </Card>
        )}
        {/* Mobile Header End */}

        <Box
          data-testid="data_container"
          display="flex"
          justifyContent="center"
          alignItems={
            selectedProperties.length === 0 ||
            smokeMetricsIsLoading ||
            (smokeMetrics && isEmptyResult)
              ? 'center'
              : 'start'
          }
          minHeight={isSmallScreen.mobile ? null : 'calc(100vh - 130px)'}
          width={1}
        >
          {selectedProperties.length === 0 ? (
            <Typography variant="h4" color="grey" textAlign="center">
              No Selected Entities
            </Typography>
          ) : (
            <>
              {smokeMetricsIsLoading && <Loading />}
              {smokeMetrics && isEmptyResult && (
                <Typography variant="h4" color="grey" textAlign="center">
                  No Data For Selected Filters
                </Typography>
              )}
              {smokeMetrics && !isEmptyResult && !smokeMetricsIsLoading && (
                <Grid container spacing={2} columns={12} mb={4} width={1}>
                  {selectedView === METRICS_DISPLAY_TABLE && groupByGroupingType && (
                    <GroupedByGroupingTypeView
                      groupedByEntityData={groupedByEntityData}
                      groupingType={selectedGrouping}
                      selectedMetrics={selectedMetrics}
                      showPercentageChange={showPercentageChange}
                    />
                  )}
                  {((selectedView === METRICS_DISPLAY_TABLE && !groupByGroupingType) ||
                    selectedView === METRICS_DISPLAY_GRAPH) && (
                    <GroupedByMetricView
                      smokeMetrics={smokeMetrics}
                      selectedMetrics={selectedMetrics}
                      selectedView={selectedView}
                      selectedInterval={selectedInterval}
                      dateRangeValue={dateRangeValue}
                      showTotal={showTotal}
                      showPercentageChange={showPercentageChange}
                    />
                  )}
                </Grid>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  )
}
